import { useQuery } from '@tanstack/react-query'
import produce, { Draft } from 'immer'
import { useCallback, useMemo } from 'react'
import { toast } from 'react-toastify'

import { HerbicideTreatment } from '@/services/get-herbicide-treatments'
import { getInfestationFromDiagnosis } from '@/services/get-infestation-from-diagnosis'
import { queryClient } from '@/services/query-client'
import { Layer } from '@/types/weed-diagnosis'

export type UseGetInfestationFromDiagnosisResponse = NonNullable<
  ReturnType<typeof useGetInfestationFromDiagnosis>['data']
>

export interface LayerMap extends Layer {
  buffer: number
  buffered: boolean
  mergedName: string
  removed?: boolean
  herbicideTreatment?: HerbicideTreatment | null
  fillColor?: boolean
}

export type MergedLayerMap = Pick<
  LayerMap,
  'name' | 'colorPalette' | 'buffer' | 'buffered' | 'herbicideTreatment'
>

type QueryMapper = (
  queryData: Draft<UseGetInfestationFromDiagnosisResponse>,
) => Draft<UseGetInfestationFromDiagnosisResponse> | void

export const GET_INFESTATION_FROM_DIAGNOSIS_QUERY =
  'GET_INFESTATION_FROM_DIAGNOSIS'

export function useGetInfestationFromDiagnosis(
  culture: string,
  diagnosisId?: string,
) {
  const queryKey = useMemo(
    () => [GET_INFESTATION_FROM_DIAGNOSIS_QUERY, culture, diagnosisId],
    [culture, diagnosisId],
  )

  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const data = await getInfestationFromDiagnosis(culture, diagnosisId!, {
        signal,
      })
      const layersMap: LayerMap[] = []

      const groups = data.infestations.map((infestation) => ({
        name: infestation.name,
        area: infestation.area,
        layers: infestation.layers.map((layer) => {
          layersMap.push({
            ...layer,
            mergedName: '',
            removed: false,
            buffer: 10,
            buffered: true,
            fillColor: true,
          })

          return {
            name: layer.name,
            area: layer.area,
            colorPalette: layer.colorPalette,
          }
        }),
      }))

      const flightDateLabel = new Date(
        `${data.mosaic.date}T00:00`,
      ).toLocaleDateString('pt-BR')
      const concludedAtLabel = new Date(data.concluded_at).toLocaleDateString(
        'pt-BR',
      )

      return {
        perimeter: data.perimeter,
        thumbnail: data.thumbnail,
        totalInfestationArea: data.total_infestation_area,
        totalArea: data.total_area,
        layersMap,
        mergedLayersMap: [] as MergedLayerMap[],
        summary: {
          title: 'Dados da infestação',
          subtitle: 'Infestação total',
          head: data.total_infestation_area,
          groups: groups,
        },
        id: data.id,
        demandName: data.demand_name,
        flightDate: data.mosaic.date,
        flightDateLabel,
        concludedAt: data.concluded_at,
        concludedAtLabel,
        status: data.status,
        gsd: data.mosaic.gsd,
        datum: data.mosaic.datum,
      }
    },
    cacheTime: 0,
    enabled: !!diagnosisId,
    onError(e: any) {
      toast.error(
        'Ocorreu um erro ao buscar o resumo de infestação: ' + e.message,
      )
    },
  })

  const updateLayersFromQuery = useCallback(
    (queryMapper: QueryMapper) => {
      queryClient.setQueryData<UseGetInfestationFromDiagnosisResponse>(
        queryKey,
        produce((draft) => queryMapper(draft!)),
      )
    },
    [queryKey],
  )

  return { ...query, updateLayersFromQuery }
}
